import React, { useState, useEffect, useId, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ListIcon, SkepticLogo, Button, EmailFormField, AlertMessage } from './SkepticParts';
import { stripe_checkout_session } from '../api/api_calls';



const BecomePaidMemberModal = ({api_server_url}) => {
    const uniqueId = useId();
    const emailInputRef = useRef(null); // Ref to the EmailFormField's input

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalIntent, setModalIntent] = useState(null);
    const [tierData, setTierData] = useState(null);
    const [userData, setUserData] = useState(null);

    const [buttonState, setButtonState] = useState('disabled'); //State can be default, loading, disabled 
    const [isFormDisabled, setisFormDisabled] = useState(false);
    const [emailValue, setEmailValue] = useState('');

    const [alertMessage, setAlertMessage] = useState({visible:false, message:{}});

    let modal_title = null;

    switch (modalIntent) {
        case 'paid_member':
            modal_title = tierData.name;
            break;
    }

    // Focus the input in EmailFormField when the modal opens
    useEffect(() => {
      if (isModalOpen) {
        emailInputRef.current?.focus();
        document.getElementsByTagName('body')[0].style.overflow = 'hidden';
      }else{
        document.getElementsByTagName('body')[0].style.overflow = '';
      }
    }, [isModalOpen]);

    useEffect(() => {
        const handleOpenModal = (event) => {
          setModalIntent(event.detail.intention);
          setTierData(event.detail.tier_data);
          setUserData(event.detail.user_data);
          setIsModalOpen(true);

          if(event.detail.user_data.client_email.length > 0){
            setEmailValue(event.detail.user_data.client_email);
            setisFormDisabled(true);
            setButtonState('default');
          }

        };
        
        window.addEventListener('openBecomePaidMemberModal', handleOpenModal);    
    
        return () => {
          window.removeEventListener('openBecomePaidMemberModal', handleOpenModal);
        };
    }, []);


      const handleModalClose = () => {
        setIsModalOpen(false);
      };

      const handleAlertVisibility = (shown) => {
        if(!shown){
          setAlertMessage({visible:false, message:{}});
        }
      }

      const handleButtonStateFromForm = (is_active, value) => {
        if(is_active){          
          setButtonState('default');
          setEmailValue(value);
        }else{
          setButtonState('disabled');
        }
      }

      const stripe_checkout = async() => {
        setButtonState('loading');
        try {
            //await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulate Stripe checkout
            if(emailValue.length === 0) throw new Error('Email is missing!');

            const userDataWithUpdatedEmail = { ...userData, client_email: emailValue };
            const response = await stripe_checkout_session(api_server_url, modalIntent, tierData, userDataWithUpdatedEmail);

            if (response.message === 'ok') {
                setAlertMessage({ visible:true, message:{type: 'success', title: 'Stripe', text: 'Redirecting to checkout...', duration: 5000} });
                setTimeout(()=>{ window.location.href = response.data }, 500);
            } else {
              setAlertMessage({ visible:true, message:{type: 'danger', title: 'Error', text: response.data, duration: 5000} });;
            }            

        } catch (error) {
            console.log('[x] err in stripe checkout -> ', error.message);
            setAlertMessage({ visible:true, message:{type: 'danger', title: 'Error', text: error.message, duration: 5000} });
        }finally{
          setButtonState('default');
        }
      }

    const modalVariants = {
        hidden: { opacity: 0, translateY: "2.5rem", scale: 0.95 },
        visible: { opacity: 1, translateY: "0rem", scale: 1 },
        exit: { opacity: 0, translateY: "2.5rem", scale: 0.95 },
      };

    if (!isModalOpen) return;      
    return(
        <>            
            <div onClick={handleModalClose} className={`${isModalOpen ? 'flex' : 'hidden'} skp_modal_wrapper`} role="dialog" aria-modal="true" aria-labelledby={modal_title}>
            <section className='flex justify-center  w-full min-h-screen max-h-screen overflow-y-auto' style={{scrollbarWidth: 'none'}}>
                <motion.div 
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={{
                  duration: 0.2,
                  ease: [0.4, 0, 0.2, 1],
                  delay: 0.075,
                }} 
                className='skp_modal_window' onClick={(e) => e.stopPropagation()}>
                    <motion.div whileHover={{ scale: 1.05, rotate: 12, backgroundColor: "#ff5f5f" }} onClick={handleModalClose} 
                      role="button" className='size-10 bg-skeptic-primary rounded-full absolute top-2 right-2 sm:-top-5 sm:-right-5'>
                          <svg className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" >
                            <mask id={`mask_${uniqueId}`} style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x={0} y={0} width={24} height={24}>
                              <rect width={24} height={24} fill="#D9D9D9" />
                            </mask>
                            <g mask={`url(#mask_${uniqueId})`}><path d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z" fill="white"/></g>
                          </svg>
                    </motion.div>
                    <AlertMessage show={alertMessage.visible} message_body={alertMessage.message} handleAlertVisibility={handleAlertVisibility} />
                    <div className='px-12 flex flex-col justify-center items-center gap-8'>
                      <header className="flex w-full flex-col justify-center">
                        <SkepticLogo/>
                        <h3 className='text-2xl text-center mt-12 text-skeptic-charcoal dark:text-skeptic-foreground-dark'>{modal_title}</h3>
  
                        <div className='flex justify-center mt-8'>
                          <div className='flex text-center items-center gap-2'>
                            <span className='inline-block text_24 text-skeptic-charcoal dark:text-skeptic-foreground-dark'>$</span>
                            <span className="inline-block text-[3rem] leading-none font-bold text-skeptic-charcoal dark:text-skeptic-foreground-dark">
                              {
                            (()=>{
                                if(tierData.type == 'paid'){
                                   return tierData.price / 100; 
                                }else{
                                    return 0;
                                }            
                            })()}</span>
                            <span className="inline-block leading-none text_24 text-[#6f6f6f]">/{tierData.interval}</span>
                          </div>
                        </div>

                        <div className='w-full mt-4'>
                          <ul className="pl-6 list-inside space-y-2 text-base text-skeptic-charcoal dark:text-skeptic-foreground-dark">
                              {tierData.benefits.map((benefit, index) => (
                                  <li className='flex relative py-0.5' key={index}>
                                      <ListIcon className="size-[1.125rem] absolute -left-6 top-px"/>
                                      {benefit}
                                  </li>
                              ))}            
                          </ul>
                        </div>  
                      </header>

                      <div className='flex w-full'>
                        <form className='flex w-full'>
                          <EmailFormField ref={emailInputRef} value={emailValue} disabled={isFormDisabled} handleButton={handleButtonStateFromForm}/> {/* value > 'email string', disabled > true/false */}
                        </form>
                      </div>

                      <div className='flex justify-center w-full'>
                        <Button
                        className={'w-full justify-center'}
                        text={buttonState === 'loading' ? 'Loading' : 'Subscribe'} 
                        state={buttonState} 
                        onClick={buttonState === 'default' ? stripe_checkout : null} />{/* State can be default, loading, disabled */}
                      </div>
                    </div>
                </motion.div>
                </section>          
            </div>
        </>
    )
}

export default BecomePaidMemberModal; // Default export


